/**
 * BatchPlanChooser
 *
 * Shown when a student arrives at a quiz (e.g. via shared link) but is not yet
 * enrolled in any batch that contains it. Lets them pick a batch and plan before
 * proceeding to the quiz.
 */

import { useState } from 'react';
import { BookOpen, Package, Star, Loader2, ChevronRight } from 'lucide-react';
import type { BatchWithPlans } from '@/api/batch';
import type { Plan } from '@/api/plans';
import { studentEnrollInBatch } from '@/api/batch';
import './BatchPlanChooser.css';

interface Props {
  quizId: string;
  availableBatches: BatchWithPlans[];
  onEnrolled: () => void;
}

function formatPrice(amount: number | string, currency: string) {
  const n = Number(amount);
  if (n === 0) return 'Free';
  if (currency === 'INR') return `\u20B9${n.toFixed(0)}`;
  return `${currency} ${n.toFixed(0)}`;
}

export function BatchPlanChooser({ quizId: _quizId, availableBatches, onEnrolled }: Props) {
  const [selectedBatch, setSelectedBatch] = useState<BatchWithPlans | null>(
    availableBatches.length === 1 ? availableBatches[0] : null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const freePlans = (batch: BatchWithPlans) =>
    batch.plans.filter((p) => Number(p.price_amount) === 0);
  const paidPlans = (batch: BatchWithPlans) =>
    batch.plans.filter((p) => Number(p.price_amount) > 0);

  async function handleFreeEnroll(batch: BatchWithPlans, plan: Plan | null) {
    setLoading(true);
    setError(null);
    try {
      await studentEnrollInBatch(batch.id);
      if (plan) {
        // Free plan is auto-activated by the enroll endpoint
      }
      onEnrolled();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enrollment failed. Please try again.');
      setLoading(false);
    }
  }

  async function handlePaidEnroll(batch: BatchWithPlans, plan: Plan) {
    setLoading(true);
    setError(null);
    try {
      // First enroll in the batch
      await studentEnrollInBatch(batch.id);
      // Then redirect to purchase the plan
      window.location.href = `/student/plans/${plan.id}/purchase?batch_id=${batch.id}`;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enrollment failed. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="bpc-overlay">
      <div className="bpc-modal">
        <div className="bpc-header">
          <BookOpen size={24} className="bpc-header__icon" />
          <div>
            <h2 className="bpc-header__title">Choose Your Batch</h2>
            <p className="bpc-header__subtitle">
              Select a batch and plan to access this quiz
            </p>
          </div>
        </div>

        {error && <div className="bpc-error">{error}</div>}

        {!selectedBatch && availableBatches.length > 1 && (
          <div className="bpc-batch-list">
            <p className="bpc-section-label">Available Batches</p>
            {availableBatches.map((batch) => (
              <button
                key={batch.id}
                className="bpc-batch-card"
                onClick={() => setSelectedBatch(batch)}
                disabled={loading}
              >
                <div className="bpc-batch-card__info">
                  <span className="bpc-batch-card__name">
                    {batch.name}
                    {batch.is_default && (
                      <span className="bpc-badge bpc-badge--default">Default</span>
                    )}
                  </span>
                  <span className="bpc-batch-card__course">{batch.course_name}</span>
                  {batch.description && (
                    <span className="bpc-batch-card__desc">{batch.description}</span>
                  )}
                </div>
                <ChevronRight size={16} className="bpc-batch-card__arrow" />
              </button>
            ))}
          </div>
        )}

        {selectedBatch && (
          <div className="bpc-plan-section">
            {availableBatches.length > 1 && (
              <button
                className="bpc-back-btn"
                onClick={() => setSelectedBatch(null)}
                disabled={loading}
              >
                ← Back to batches
              </button>
            )}

            <div className="bpc-selected-batch">
              <span className="bpc-section-label">Batch:</span>
              <span className="bpc-selected-batch__name">
                {selectedBatch.name}
                {selectedBatch.is_default && (
                  <span className="bpc-badge bpc-badge--default">Default</span>
                )}
              </span>
              <span className="bpc-selected-batch__course">{selectedBatch.course_name}</span>
            </div>

            {selectedBatch.plans.length === 0 ? (
              // No plans required — just enroll
              <div className="bpc-plan-list">
                <p className="bpc-section-label">Access</p>
                <button
                  className="bpc-plan-card bpc-plan-card--free"
                  onClick={() => handleFreeEnroll(selectedBatch, null)}
                  disabled={loading}
                >
                  <div className="bpc-plan-card__left">
                    <Package size={18} />
                    <div>
                      <span className="bpc-plan-card__name">Free Access</span>
                      <span className="bpc-plan-card__desc">No plan required for this batch</span>
                    </div>
                  </div>
                  <span className="bpc-plan-card__price">Free</span>
                  {loading ? <Loader2 size={16} className="bpc-spinner" /> : null}
                </button>
              </div>
            ) : (
              <div className="bpc-plan-list">
                {freePlans(selectedBatch).length > 0 && (
                  <>
                    <p className="bpc-section-label">Free Plans</p>
                    {freePlans(selectedBatch).map((plan) => (
                      <button
                        key={plan.id}
                        className="bpc-plan-card bpc-plan-card--free"
                        onClick={() => handleFreeEnroll(selectedBatch, plan)}
                        disabled={loading}
                      >
                        <div className="bpc-plan-card__left">
                          <Package size={18} />
                          <div>
                            <span className="bpc-plan-card__name">{plan.name}</span>
                            {plan.description && (
                              <span className="bpc-plan-card__desc">{plan.description}</span>
                            )}
                          </div>
                        </div>
                        <div className="bpc-plan-card__right">
                          <span className="bpc-plan-card__price">Free</span>
                          {loading && <Loader2 size={16} className="bpc-spinner" />}
                        </div>
                      </button>
                    ))}
                  </>
                )}

                {paidPlans(selectedBatch).length > 0 && (
                  <>
                    <p className="bpc-section-label">Paid Plans</p>
                    {paidPlans(selectedBatch).map((plan) => (
                      <button
                        key={plan.id}
                        className="bpc-plan-card bpc-plan-card--paid"
                        onClick={() => handlePaidEnroll(selectedBatch, plan)}
                        disabled={loading}
                      >
                        <div className="bpc-plan-card__left">
                          <Star size={18} />
                          <div>
                            <span className="bpc-plan-card__name">{plan.name}</span>
                            {plan.description && (
                              <span className="bpc-plan-card__desc">{plan.description}</span>
                            )}
                          </div>
                        </div>
                        <div className="bpc-plan-card__right">
                          <span className="bpc-plan-card__price">
                            {formatPrice(plan.price_amount, plan.currency)}
                          </span>
                          {loading && <Loader2 size={16} className="bpc-spinner" />}
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
